/**
 * The supermarket run: a walk-in supermarket in every city, real things on the shelves (made-up brands, local
 * flavour per country), a cart you fill by picking things off the shelves, a till. What you buy goes into your
 * kitchen (the pantry) and you eat it at home later; a jollof kit makes cooking free of the ingredients charge.
 * Prices are Lagos prices scaled to where you are (localPrice), like everything else you buy.
 */
import { advance, spend, type LifeEvent, type LifeState, type NeedKey } from './life.ts';
import { hereCountry, localPrice } from './character.ts';
import type { Country } from './travel.ts';

export type Aisle = 'fresh' | 'bakery' | 'pantry' | 'drinks' | 'frozen' | 'snacks' | 'home';
export interface Grocery {
  id: string;
  label: string;
  /** Lagos price (naira); the till charges localPrice */
  ngn: number;
  aisle: Aisle;
  /** what eating/using one does (none: an ingredient or a household thing) */
  needs?: Partial<Record<NeedKey, number>>;
  /** how it looks on the shelf (the game draws it): can, bottle, box, bag, jar, carton, fruit, loaf, tray */
  shape: 'can' | 'bottle' | 'box' | 'bag' | 'jar' | 'carton' | 'fruit' | 'loaf' | 'tray';
  colour: number;
  /** a cooking kit: used up by that dish instead of paying for ingredients */
  kit?: 'jollof';
}

const g = (id: string, label: string, ngn: number, aisle: Aisle, shape: Grocery['shape'], colour: number, needs?: Grocery['needs'], kit?: 'jollof'): Grocery => ({ id, label, ngn, aisle, shape, colour, needs, kit });

/** On every shelf, everywhere. */
const BASICS: Grocery[] = [
  g('water', 'Bottled water (6-pack)', 2_400, 'drinks', 'bottle', 0x8fc7e8, { hunger: 2, energy: 3 }),
  g('juice', 'Orange juice', 2_800, 'drinks', 'carton', 0xf2a03a, { hunger: 6, fun: 3 }),
  g('milk', 'Milk', 2_200, 'fresh', 'carton', 0xf4f4f0, { hunger: 8 }),
  g('eggs', 'Eggs (a dozen)', 3_600, 'fresh', 'tray', 0xe8d6b0, { hunger: 18 }),
  g('bananas', 'Bananas', 1_500, 'fresh', 'fruit', 0xf2d23a, { hunger: 10 }),
  g('apples', 'Apples', 2_600, 'fresh', 'fruit', 0xc0392b, { hunger: 9 }),
  g('bread', 'Sliced bread', 1_400, 'bakery', 'loaf', 0xd9a35a, { hunger: 20 }),
  g('cereal', 'Cornflakes', 3_800, 'pantry', 'box', 0xe6b422, { hunger: 16 }),
  g('rice', 'Rice (5 kg)', 9_500, 'pantry', 'bag', 0xf2efe6, { hunger: 30 }),
  g('crisps', 'Crisps', 900, 'snacks', 'bag', 0xd63a2f, { hunger: 6, fun: 5 }),
  g('chocolate', 'Chocolate bar', 1_200, 'snacks', 'box', 0x5a2f1c, { hunger: 5, fun: 8 }),
  g('icecream', 'Ice cream (tub)', 4_500, 'frozen', 'tray', 0xf2dcc8, { hunger: 8, fun: 12 }),
  g('soap', 'Shower gel', 2_200, 'home', 'bottle', 0x3aa6a0, { hygiene: 25 }),
  g('toothpaste', 'Toothpaste', 1_300, 'home', 'box', 0x2e86de, { hygiene: 15 }),
  g('tissue', 'Toilet roll (4)', 2_000, 'home', 'bag', 0xffffff),
  g('pet-food', 'Pet food (a week)', 6_500, 'home', 'bag', 0x8a5a2a),
];

/** What makes each country's supermarket that country's. */
const LOCAL: Record<Country, Grocery[]> = {
  nigeria: [
    g('noodles-ng', 'Instant noodles (carton of 10)', 4_200, 'pantry', 'box', 0xf2c12e, { hunger: 22, fun: 3 }),
    g('jollof-kit', 'Jollof kit (rice, tomato paste, peppers, seasoning)', 2_600, 'pantry', 'bag', 0xc0392b, undefined, 'jollof'),
    g('garri', 'Garri (2 kg)', 2_400, 'pantry', 'bag', 0xf4e7c0, { hunger: 24 }),
    g('plantain-chips', 'Plantain chips', 800, 'snacks', 'bag', 0xe8b23a, { hunger: 6, fun: 5 }),
    g('malt', 'Malt drink (can)', 700, 'drinks', 'can', 0x5a2a12, { hunger: 5, energy: 4, fun: 3 }),
    g('zobo', 'Zobo (bottle)', 900, 'drinks', 'bottle', 0x7a1530, { hunger: 3, fun: 4 }),
    g('meatpie', 'Meat pie', 1_200, 'bakery', 'box', 0xd9a35a, { hunger: 14, fun: 3 }),
  ],
  france: [
    g('baguette', 'Baguette', 600, 'bakery', 'loaf', 0xd9a35a, { hunger: 18, fun: 2 }),
    g('croissants', 'Croissants (4)', 2_200, 'bakery', 'box', 0xe0a64b, { hunger: 16, fun: 6 }),
    g('cheese', 'Camembert', 2_800, 'fresh', 'box', 0xf4efe0, { hunger: 12, fun: 6 }),
    g('yoghurt', 'Yoghurts (4)', 1_800, 'fresh', 'tray', 0xf4f4f0, { hunger: 8 }),
    g('ready-quiche', 'Quiche lorraine', 3_500, 'frozen', 'tray', 0xe8c87a, { hunger: 26, fun: 4 }),
    g('mineral', 'Sparkling water', 900, 'drinks', 'bottle', 0x3a8f5a, { hunger: 1, energy: 2 }),
  ],
  uae: [
    g('karak', 'Karak tea (instant)', 1_100, 'drinks', 'box', 0xc98a3a, { hunger: 2, energy: 6, fun: 3 }),
    g('dates', 'Medjool dates', 3_400, 'snacks', 'box', 0x5a2e18, { hunger: 10, fun: 5 }),
    g('khubz', 'Khubz (Arabic bread)', 900, 'bakery', 'bag', 0xe2c08a, { hunger: 14 }),
    g('hummus', 'Hummus', 1_600, 'fresh', 'tray', 0xe8d4a0, { hunger: 12, fun: 3 }),
    g('labneh', 'Labneh', 1_900, 'fresh', 'jar', 0xf4f0e6, { hunger: 10, fun: 3 }),
    g('laban', 'Laban (yoghurt drink)', 700, 'drinks', 'carton', 0xf0f4f8, { hunger: 4, energy: 2 }),
  ],
  italy: [
    g('focaccia', 'Focaccia', 1_200, 'bakery', 'loaf', 0xd9a35a, { hunger: 16, fun: 3 }),
    g('pasta', 'Pasta (500 g)', 900, 'pantry', 'box', 0x2e5fa8, { hunger: 24 }),
    g('pecorino', 'Pecorino toscano', 3_200, 'fresh', 'box', 0xf1e6c8, { hunger: 12, fun: 6 }),
    g('prosciutto', 'Prosciutto (sliced)', 3_800, 'fresh', 'tray', 0xd9767a, { hunger: 14, fun: 5 }),
    g('cantucci', 'Cantucci (almond biscuits)', 1_900, 'snacks', 'bag', 0xd8b070, { hunger: 8, fun: 5 }),
    g('acqua', 'Sparkling water', 700, 'drinks', 'bottle', 0x3a8f5a, { hunger: 1, energy: 2 }),
  ],
  usa: [
    g('pizza-frozen', 'Frozen pizza', 4_200, 'frozen', 'box', 0xc0392b, { hunger: 30, fun: 6 }),
    g('pbj', 'Peanut butter', 3_000, 'pantry', 'jar', 0xb5763a, { hunger: 14 }),
    g('mac', 'Mac and cheese (box)', 1_600, 'pantry', 'box', 0x2e86de, { hunger: 22, fun: 4 }),
    g('soda', 'Soda (12-pack)', 5_500, 'drinks', 'can', 0xc0392b, { hunger: 4, fun: 6, energy: 3 }),
    g('bagels', 'Bagels (6)', 2_600, 'bakery', 'bag', 0xd9a35a, { hunger: 18 }),
    g('jerky', 'Beef jerky', 2_400, 'snacks', 'bag', 0x6a3a1c, { hunger: 10, fun: 3 }),
  ],
  china: [
    g('dumplings-frozen', 'Frozen dumplings', 3_000, 'frozen', 'bag', 0xf4f0e6, { hunger: 28, fun: 5 }),
    g('greentea-cn', 'Green tea (bottle)', 800, 'drinks', 'bottle', 0x8bc34a, { hunger: 1, energy: 4 }),
    g('instant-cn', 'Beef noodle cup', 900, 'pantry', 'jar', 0xd63a2f, { hunger: 18, fun: 3 }),
    g('mooncake', 'Mooncakes (box)', 4_800, 'bakery', 'box', 0xc0392b, { hunger: 14, fun: 8 }),
    g('soy', 'Soy milk', 900, 'fresh', 'carton', 0xf2e6c8, { hunger: 7 }),
    g('sunflower', 'Sunflower seeds', 1_000, 'snacks', 'bag', 0x3a3a3a, { hunger: 4, fun: 4 }),
  ],
  japan: [
    g('onigiri', 'Onigiri (salmon)', 700, 'fresh', 'tray', 0x1b1b1b, { hunger: 12, fun: 3 }),
    g('bento', 'Bento box', 3_200, 'fresh', 'tray', 0xc0392b, { hunger: 30, fun: 5 }),
    g('ramen-cup', 'Cup ramen', 900, 'pantry', 'jar', 0xf2c12e, { hunger: 18, fun: 3 }),
    g('greentea-jp', 'Green tea (bottle)', 800, 'drinks', 'bottle', 0x5a9a3a, { hunger: 1, energy: 4 }),
    g('melonpan', 'Melon pan', 900, 'bakery', 'loaf', 0xe8d27a, { hunger: 12, fun: 5 }),
    g('pocky', 'Chocolate sticks', 900, 'snacks', 'box', 0xd63a2f, { hunger: 4, fun: 7 }),
  ],
};

/** What's on the shelves here (local things first). */
export function groceriesHere(s: LifeState): Grocery[] { return [...LOCAL[hereCountry(s)], ...BASICS]; }
export const groceryById = (id: string): Grocery | undefined => [...BASICS, ...Object.values(LOCAL).flat()].find((x) => x.id === id);
export const groceryPrice = (s: LifeState, x: Grocery) => localPrice(s, x.ngn, 50);

/** Your kitchen: what you've bought and not used yet (id → how many). */
export function pantryOf(s: LifeState): Record<string, number> { return (s.pantry ??= {}); }

/** At the till: pay for the cart, it goes into your kitchen. Returns an error, or null. */
export function checkout(s: LifeState, cart: Record<string, number>): { error: string | null; total: number; events: LifeEvent[] } {
  const lines = Object.entries(cart).filter(([, n]) => n > 0).map(([id, n]) => [groceryById(id), n] as const).filter((x): x is readonly [Grocery, number] => !!x[0]);
  const total = lines.reduce((t, [x, n]) => t + groceryPrice(s, x) * n, 0);
  if (!lines.length) return { error: 'Your cart is empty.', total: 0, events: [] };
  if (total > s.money) return { error: `That comes to more than you have (you're short ${Math.ceil(total - s.money).toLocaleString()}).`, total, events: [] };
  spend(s, total, 'Supermarket');
  const p = pantryOf(s);
  for (const [x, n] of lines) p[x.id] = (p[x.id] ?? 0) + n;
  s.stats.groceryRuns = (s.stats.groceryRuns ?? 0) + 1;
  const events = advance(s, 10 + lines.length * 2, { busy: true });
  return { error: null, total, events };
}

/** Eat or use one thing from your kitchen (at home). */
export function useFromPantry(s: LifeState, id: string): { ok: boolean; line: string; events: LifeEvent[] } {
  const p = pantryOf(s), x = groceryById(id);
  if (!x || !p[id]) return { ok: false, line: '', events: [] };
  p[id]--;
  if (p[id] <= 0) delete p[id];
  for (const [k, v] of Object.entries(x.needs ?? {}) as [NeedKey, number][]) s.needs[k] = Math.max(0, Math.min(100, s.needs[k] + v));
  const events = advance(s, x.aisle === 'home' ? 10 : 15);
  return { ok: true, line: x.aisle === 'home' ? `${x.label}: done.` : `You have the ${x.label.toLowerCase().replace(/\s*\(.*\)$/, '')}.`, events };
}

/** Cooking with a kit you bought: it's used up instead of paying for the ingredients. */
export function useKit(s: LifeState, kit: 'jollof'): boolean {
  const p = pantryOf(s), id = Object.keys(p).find((k) => groceryById(k)?.kit === kit);
  if (!id) return false;
  p[id]--;
  if (p[id] <= 0) delete p[id];
  return true;
}

// --- delivery: order on the phone (from the fridge), it comes to the door about an hour later --------------------

export const deliveryFee = (s: LifeState) => localPrice(s, 1_500, 50);
export interface Delivery { at: number; cart: Record<string, number> }

/** Pay now (the cart and the fee); it arrives in your kitchen in about an hour. */
export function orderGroceries(s: LifeState, cart: Record<string, number>): { error: string | null; total: number; at: number } {
  const lines = Object.entries(cart).filter(([id, n]) => n > 0 && groceryById(id));
  if (!lines.length) return { error: 'Nothing in the order.', total: 0, at: 0 };
  const total = lines.reduce((t, [id, n]) => t + groceryPrice(s, groceryById(id)!) * n, 0) + deliveryFee(s);
  if (total > s.money) return { error: `That comes to more than you have (you're short ${Math.ceil(total - s.money).toLocaleString()}).`, total, at: 0 };
  spend(s, total, 'Groceries (delivery)');
  const at = s.minutes + 50 + Math.floor(Math.random() * 25);
  (s.deliveries ??= []).push({ at, cart: Object.fromEntries(lines) });
  return { error: null, total, at };
}

/** Deliveries that are due land in the kitchen (called from the life loop). */
export function deliverGroceries(s: LifeState, events: LifeEvent[]) {
  if (!s.deliveries?.length) return;
  const due = s.deliveries.filter((d) => d.at <= s.minutes);
  if (!due.length) return;
  s.deliveries = s.deliveries.filter((d) => d.at > s.minutes);
  const p = pantryOf(s);
  for (const d of due) for (const [id, n] of Object.entries(d.cart)) p[id] = (p[id] ?? 0) + n;
  events.push({ t: s.minutes, kind: 'info', text: 'Your groceries were delivered. They\'re in the fridge.' });
}

/** What's ready to eat in the fridge (not kits, not the pet's food). */
export function fridgeItems(s: LifeState): { item: Grocery; n: number }[] {
  return Object.entries(pantryOf(s)).map(([id, n]) => ({ item: groceryById(id)!, n })).filter((x) => x.item && !x.item.kit && x.item.id !== 'pet-food' && x.item.aisle !== 'home');
}
